import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronIcon } from "vega-icons";

import { ThemedIconButton } from "@vega-core/components/Buttons/ThemedIconButton";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

export const TABLE_SCROLL_STEP_PX = 8; // Adjust this to slow down or speed up the scroll

type ScrollIndicatorButtonProps = {
  canScrollHorizontally?: boolean;
  className?: string;
  scrollRef: React.RefObject<HTMLDivElement | null>;
};

export const ScrollIndicatorButton = ({
  canScrollHorizontally,
  className,
  scrollRef,
}: ScrollIndicatorButtonProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const scrollElementRef = useRef<HTMLElement | null>(null);
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const scrollInterval = 16; // ~60fps

  // Find the actual horizontally scrollable element within the container
  const resolveScrollableElement = useCallback((): HTMLElement | null => {
    const container = scrollRef.current;

    if (!container) return null;

    // Prefer descendants with explicit overflow styles
    const candidates = [
      container,
      ...Array.from(
        container.querySelectorAll(
          '.overflow-auto, .overflow-x-auto, [style*="overflow-x"], [style*="overflow"]',
        ),
      ),
    ] as HTMLElement[];

    for (const candidate of candidates) {
      if (candidate.scrollWidth > candidate.clientWidth) {
        return candidate;
      }
    }

    return container;
  }, [scrollRef]);

  const checkScrollState = useCallback(() => {
    const element = scrollElementRef.current;

    if (!element) return;

    const { scrollLeft, scrollWidth, clientWidth } = element;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

    const visible =
      typeof canScrollHorizontally === "boolean"
        ? canScrollHorizontally
        : maxScrollLeft > 0;

    setIsVisible(visible);
    // can scroll right if not at the far right
    const canRight = scrollLeft < maxScrollLeft - 1;
    setCanScrollRight(canRight);
  }, [canScrollHorizontally]);

  // Resolve the scrollable element and subscribe to events
  useEffect(() => {
    scrollElementRef.current = resolveScrollableElement();

    const element = scrollElementRef.current;

    if (!element) return;

    // Set optimistic state on mount so the button appears immediately when we know scrolling is possible
    if (canScrollHorizontally) {
      setIsVisible(true);
      setCanScrollRight(true);
    }

    checkScrollState();

    // Ensure visibility/placement checks happen after layout settles
    const rafId = requestAnimationFrame(() => {
      checkScrollState();
    });
    const timeoutId = setTimeout(() => {
      checkScrollState();
    }, 150);

    const handleScroll = () => {
      checkScrollState();
    };

    const resizeObserver = new ResizeObserver(() => {
      // re-resolve on resize in case layout changes
      scrollElementRef.current = resolveScrollableElement();
      checkScrollState();
    });

    const container = scrollRef.current;

    element.addEventListener("scroll", handleScroll);

    if (container) resizeObserver.observe(container);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      element.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [
    resolveScrollableElement,
    checkScrollState,
    canScrollHorizontally,
    scrollRef,
  ]);

  // If canScrollHorizontally prop flips true later, reveal button immediately and re-check
  useEffect(() => {
    if (canScrollHorizontally) {
      setIsVisible(true);
      setCanScrollRight(true);

      const raf = requestAnimationFrame(() => {
        checkScrollState();
      });
      const t = setTimeout(() => {
        checkScrollState();
      }, 150);

      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(t);
      };
    }
  }, [canScrollHorizontally, checkScrollState]);

  // Start slow scrolling when button is pressed
  const handleMouseDown = useCallback(() => {
    const element = scrollElementRef.current;

    if (!element) return;

    const tick = () => {
      const { scrollLeft, scrollWidth, clientWidth } = element;
      const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

      if (scrollLeft < maxScrollLeft) {
        element.scrollLeft = Math.min(
          scrollLeft + TABLE_SCROLL_STEP_PX,
          maxScrollLeft,
        );

        // update state eagerly to avoid flicker
        setCanScrollRight(element.scrollLeft < maxScrollLeft - 1);
      } else {
        setCanScrollRight(false);

        if (scrollIntervalRef.current) {
          clearInterval(scrollIntervalRef.current);
          scrollIntervalRef.current = null;
        }
      }
    };

    // safety: clear any existing interval first
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
    }

    scrollIntervalRef.current = setInterval(tick, scrollInterval);
  }, []);

  // Stop scrolling when button is released or pointer leaves
  const stopScrolling = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  // Cleanup interval on unmount
  useEffect(() => () => stopScrolling(), [stopScrolling]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={cnTwMerge(
        "z-10 absolute top-1/2 -translate-y-1/2 -right-4",
        className,
      )}
    >
      <ThemedIconButton
        ariaLabel="Scroll table to the right"
        className={cnTwMerge(
          "shadow-card border border-border/primary rounded-full",
          !canScrollRight && "opacity-50 cursor-not-allowed",
        )}
        dataTestId="table-scroll-indicator"
        disabled={!canScrollRight}
        icon={<ChevronIcon className=" rotate-270" />}
        onMouseDown={handleMouseDown}
        onMouseLeave={stopScrolling}
        onMouseUp={stopScrolling}
        variant="background"
      />
    </div>
  );
};
